import type {
  ICertificateDetails,
  ICertificateDetailsItem,
  ICertificateItem,
  ICertificateItemTemplate,
} from '@share/common-types';
import { CertificateStatusEnum } from '@share/common-types';

import type { CertificateTemplateDetailViewData } from '../certificate-template-detail-view/types';

function mapCertificateItemsToDetailTemplates(
  items: ICertificateItem[],
  templateId: string,
): ICertificateItemTemplate[] {
  return items.map((item) => ({
    id: item.id,
    type: item.type,
    name: item.name,
    nights: item.nights,
    internalPrice: item.internalPrice,
    publicPrice: item.publicPrice,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    certificateTemplateId: templateId,
    description: '',
  }));
}

function estimateValidityMonths(purchasedAt: string, expiresAt: string): number {
  const start = new Date(purchasedAt);
  const end = new Date(expiresAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 12;
  }
  const diffMs = end.getTime() - start.getTime();
  const months = Math.round(diffMs / (30.44 * 24 * 60 * 60 * 1000));
  return Math.max(1, months);
}

function mapRedemptionStatusToCertificateItemStatus(
  redemptionStatus: string,
): CertificateStatusEnum {
  switch (redemptionStatus) {
    case 'Redeemed':
      return CertificateStatusEnum.Redeemed;
    case 'Expired':
      return CertificateStatusEnum.Expired;
    case 'Canceled':
    case 'Cancelled':
      return CertificateStatusEnum.Canceled;
    default:
      return CertificateStatusEnum.Active;
  }
}

function mapDetailsItemToCertificateItem(params: {
  certificateId: string;
  item: ICertificateDetailsItem;
  createdAtFallback: string;
}): ICertificateItem {
  const { certificateId, item, createdAtFallback } = params;
  return {
    id: item.id,
    certificateId,
    type: item.productType,
    name: '',
    nights: item.nights,
    internalPrice: 0,
    publicPrice: 0,
    createdAt: createdAtFallback,
    updatedAt: undefined,
    status: mapRedemptionStatusToCertificateItemStatus(item.redemptionStatus),
  };
}

export function mapCertificateDetailsToDetailViewData(
  cert: ICertificateDetails,
): CertificateTemplateDetailViewData {
  const detailsItems = cert.certificateItems ?? [];
  const certificateItems: ICertificateItem[] = detailsItems.map((item) =>
    mapDetailsItemToCertificateItem({
      certificateId: cert.id,
      item,
      createdAtFallback: cert.purchasedAt,
    }),
  );

  const itemTemplates: ICertificateItemTemplate[] = mapCertificateItemsToDetailTemplates(
    certificateItems,
    cert.id,
  );

  return {
    name: cert.name,
    validityMonths: estimateValidityMonths(cert.purchasedAt, cert.expiresAt),
    totalPrice: cert.totalPrice,
    savings: cert.savings,
    itemTemplates,
    eligibleCruiseLines: cert.eligibleCruiseLines ?? [],
    imageUrl: cert.templateImage,
    url: '',
    descriptionContent: cert.description,
    averageRetailPrice: cert.totalPrice,
    isCertificate: true,
    certificateCode: cert.certificateCode,
    certificateStatus: cert.status,
    certificatePurchasedAt: cert.purchasedAt,
    certificateExpiresAt: cert.expiresAt,
    certificateItems,
    certificateUpdatedAt: cert.updatedAt,
  };
}
